import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'

const DESKTOP_LINKS = [
  { href: '#features', label: 'nav.features' },
  { href: '#faq', label: 'nav.faq' },
  { href: '/blog', label: 'nav.blog' },
]

export function Navbar() {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const isAr = i18n.language === 'ar'

  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const toggleLang = useCallback(() => {
    i18n.changeLanguage(isAr ? 'en' : 'ar')
  }, [i18n, isAr])

  return (
    <>
      {/* ── Desktop / Tablet Navbar ── */}
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-[var(--banner-h,0px)] inset-x-0 z-50 hidden lg:block px-4 pt-3 sm:pt-4 transition-[top] duration-300"
      >
        <div
          className={`max-w-6xl mx-auto flex items-center gap-1 rounded-2xl border transition-all duration-500 ${
            !scrolled
              ? 'bg-marble-100/50 border-marble-200/40 backdrop-blur-xl shadow-sm h-16 px-4'
              : 'bg-marble-100/80 border-wine-100/30 backdrop-blur-2xl shadow-md h-14 px-3'
          }`}
        >
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 shrink-0">
              <div className={`relative rounded-xl overflow-hidden transition-all duration-500 ${!scrolled ? 'h-9 w-9' : 'h-8 w-8'}`}>
                <img src="/logo.svg" alt="SAKK" className="h-full w-full object-contain" />
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              <div className="hidden sm:flex flex-col">
                <span className={`font-extrabold leading-tight tracking-tight text-ink transition-all duration-500 ${!scrolled ? 'text-[17px]' : 'text-[15px]'}`}>
                  {t('nav.brandFull')}
                </span>
                <span className={`text-[8px] text-ink-hint font-mono tracking-[0.2em] uppercase leading-none transition-all duration-500 ${!scrolled ? 'opacity-100 h-auto' : 'opacity-0 h-0'}`}>
                  {t('footer.brandSub')}
                </span>
              </div>
            </Link>

            {/* Divider */}
            <div className="w-px h-5 bg-wine-200/30 shrink-0" />

            {/* Nav Links */}
            <nav className="flex items-center gap-0.5 flex-1 justify-center">
              {DESKTOP_LINKS.map((l) => {
                const isRoute = l.href.startsWith('/')
                const active = isRoute && location.pathname === l.href

                const cls = `relative px-4 py-1.5 rounded-xl text-[13px] font-semibold transition-all duration-200 ${
                  active
                    ? 'text-wine-800 bg-wine-800/[0.07]'
                    : 'text-ink-2 hover:text-ink hover:bg-marble-100'
                }`

                return l.href.startsWith('#') ? (
                  <a key={l.href} href={l.href} className={cls}>
                    {t(l.label)}
                  </a>
                ) : (
                  <Link key={l.href} to={l.href} className={cls}>
                    {t(l.label)}
                    {active && (
                      <motion.span
                        layoutId="nav-pill"
                        className="absolute inset-0 rounded-xl bg-wine-800/[0.07] -z-10"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                  </Link>
                )
              })}
            </nav>

            {/* Divider */}
            <div className="w-px h-5 bg-wine-200/30 shrink-0" />

            {/* Actions */}
            <div className="flex items-center gap-1 shrink-0 pe-1">
              {/* Direct download */}
              <a
                href="/download"
                download="sakk-wallet.apk"
                className="inline-flex items-center h-9 px-4 rounded-xl bg-wine-800 text-white text-[12px] font-bold hover:bg-wine-700 transition-colors"
              >
                {t('nav.downloadApp')}
              </a>
              {/* Language toggle */}
              <button
                onClick={toggleLang}
                className="h-8 px-3 rounded-xl text-[11px] font-bold text-ink-2 hover:text-ink hover:bg-marble-100 transition-all duration-200"
              >
                {isAr ? 'EN' : 'عربي'}
              </button>
            </div>
          </div>
      </motion.header>

      {/* Phones: one thumb-reach download button on the home page */}
      {location.pathname === '/' && (
        <a
          href="/download"
          download="sakk-wallet.apk"
          className="lg:hidden fixed bottom-4 inset-x-4 z-50 flex items-center justify-center gap-2 h-12 rounded-2xl bg-wine-800 text-white text-sm font-bold shadow-xl shadow-wine-900/20"
        >
          {t('hero.downloadCta')}
        </a>
      )}
    </>
  )
}
